import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MessageSquarePlus, Send, X, Pin, PinOff, CheckCircle2, Pencil, Trash2, Check, MapPin, Clock, RefreshCw, Lock, MoreHorizontal, EyeOff, VolumeX, Ban, Flag } from "lucide-react";
import ProMiniCard from "@/components/ProMiniCard";

const MiniProCard = ({ pro }) => {
  const navigate = useNavigate();
  const doFollow = async (e) => {
    e.stopPropagation();
    try { await api.post(`/users/follow/${pro.user_id}`); toast.success("Following " + pro.business_name); }
    catch { toast.error("Could not follow"); }
  };
  const doPin = async (e) => {
    e.stopPropagation();
    try { await api.post(`/boards/pin/${pro.user_id}`); toast.success("Pinned to your board"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not pin"); }
  };
  return (
    <div
      className="mt-1 mb-3 rounded-xl border border-black/10 bg-[#F2F4F2] p-3 flex items-center gap-3 cursor-pointer hover:bg-black/5"
      style={{ borderLeft: `3px solid ${pro.pin_color || "#C79B6B"}` }}
      onClick={() => navigate(`/pro/${pro.user_id}`)}
      data-testid="mini-pro-card"
    >
      {pro.avatar_url
        ? <img src={pro.avatar_url} className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow" alt=""/>
        : <div className="w-10 h-10 rounded-full bg-[#2E6B57] grid place-items-center font-bold ring-2 ring-white shadow">{pro.business_name?.[0]}</div>}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#245544] flex items-center gap-1 mb-0.5"><CheckCircle2 size={11}/> Worked with</p>
        <p className="text-sm font-bold leading-tight truncate" style={{ fontFamily: "Outfit" }}>{pro.business_name}</p>
        <p className="text-xs text-[#222]/50 truncate">{pro.category}</p>
      </div>
      <button onClick={doFollow} className="text-xs px-3 py-1 rounded-full border border-black/10 bg-white shrink-0" data-testid={`mini-follow-${pro.user_id}`}>Follow</button>
      <button onClick={doPin} className="text-xs btn-lime px-3 py-1 shrink-0" data-testid={`mini-pin-${pro.user_id}`}>Pin</button>
    </div>
  );
};

/**
 * A single feed post with config-driven reactions (clap/fire) and
 * canned-chip comments (praise / intent). Clients pick chips; the post
 * owner (pro) can reply with free text. Other pros can react but not comment.
 */
export const PostCard = ({ post, author, reactions = [], chips = { praise: [], intent: [] }, onChanged, theme, editable = false, onImageClick, context }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user && user.id === post.user_id;
  const isOtherPro = user && user.role === "pro" && !isOwner;
  const wall = !!post.post_type;
  const isRequest = post.post_type === "looking_for";
  const commentUrl = wall ? `/wall/${post.id}/comment` : `/posts/${post.id}/comment`;
  const [showChips, setShowChips] = useState(false);
  const [reply, setReply] = useState("");
  const [freeText, setFreeText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content || "");
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportNote, setReportNote] = useState("");
  const onWall = context === "wall";

  const doModeration = async (path, label) => {
    try { await api.post(path); toast.success(label); setMenuOpen(false); onChanged?.(); }
    catch { toast.error("Action failed"); }
  };
  const hidePost = async () => {
    try {
      await api.post(`/moderation/hide/${post.id}`);
      setMenuOpen(false);
      toast("Post hidden", { action: { label: "Undo", onClick: async () => { await api.delete(`/moderation/hide/${post.id}`); onChanged?.(); } } });
      onChanged?.();
    } catch { toast.error("Could not hide"); }
  };
  const submitReport = async () => {
    if (!reportReason) { toast.error("Pick a reason"); return; }
    try {
      await api.post("/moderation/report", { target_type: "post", target_id: post.id, reason: reportReason, note: reportNote });
      setReportOpen(false); setReportReason(""); setReportNote("");
      toast.success("Report submitted. Thank you for helping keep LIMYA safe.");
    } catch { toast.error("Could not submit report"); }
  };

  const postReactions = post.reactions || [];
  const postComments = post.comments || [];

  const requireAuth = () => {
    if (!user) { toast("Please sign in first"); navigate("/login"); return false; }
    return true;
  };

  const react = async (key) => {
    if (!requireAuth()) return;
    try {
      await api.post(`/posts/${post.id}/react`, { key });
      onChanged?.();
    } catch { toast.error("Could not react"); }
  };

  const comment = async (text) => {
    if (!requireAuth()) return;
    setBusy(true);
    try {
      await api.post(commentUrl, { text });
      setShowChips(false);
      setFreeText("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not comment");
    } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(commentUrl, { text: reply });
      setReply("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reply");
    } finally { setBusy(false); }
  };

  const closeRequest = async () => {
    if (!window.confirm("Close this request? It will be removed from active results but kept in your history.")) return;
    try { await api.post(`/wall/${post.id}/close`); toast.success("Request closed"); onChanged?.(); }
    catch { toast.error("Could not close"); }
  };
  const renewRequest = async () => {
    try {
      const r = await api.post(`/wall/${post.id}/renew`);
      toast.success(r.data.message || "Reposted");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not renew"); }
  };

  const removeComment = async (cid) => {
    try {
      await api.delete(`/posts/${post.id}/comment/${cid}`);
      onChanged?.();
    } catch { toast.error("Could not remove"); }
  };

  const togglePin = async () => {
    try {
      await api.post(`/posts/${post.id}/${post.pinned ? "unpin" : "pin"}`);
      onChanged?.();
    } catch { toast.error("Could not update pin"); }
  };

  const saveEdit = async () => {
    if (!editText.trim() && !post.image_url) { toast.error("Add some text"); return; }
    setBusy(true);
    try {
      await api.patch(`/posts/${post.id}`, { content: editText, image_url: post.image_url || "" });
      setEditing(false);
      toast.success("Post updated");
      onChanged?.();
    } catch { toast.error("Could not update post"); }
    finally { setBusy(false); }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted");
      onChanged?.();
    } catch { toast.error("Could not delete post"); }
  };

  const isProject = post.kind === "client_project";
  const authorName = isProject ? (author?.name || author?.business_name) : (author?.business_name || author?.name);
  const canReply = isOwner || (isProject && user && user.id === post.tagged_pro_id);

  const chipStyle = (bucket) =>
    bucket === "intent"
      ? "bg-[#FEF3C7] text-[#92400E]"
      : bucket === "reply"
      ? "bg-black/5 text-[#222]/80"
      : "bg-[#E9F0EC] text-[#245544]";

  return (
    <div
      className={`relative rounded-2xl shadow-sm border ${theme ? "p-5 pt-7" : `p-5 bg-white ${post.pinned ? "border-[#2E6B57]" : "border-black/5"}`}`}
      style={theme ? { background: theme.card || "#fff", borderColor: post.pinned ? "#2E6B57" : "rgba(0,0,0,0.06)", borderTopWidth: "3px", borderTopColor: theme.pin || "#EF4444" } : undefined}
      data-testid={`post-${post.id}`}
    >
      {theme && (
        <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 w-5 h-5 rounded-full shadow-md"
          style={{ background: theme.pin || "#EF4444", boxShadow: `0 3px 6px ${(theme.pin || "#EF4444")}55, inset 0 -2px 3px rgba(0,0,0,0.25)` }} />
      )}
      {post.pinned && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#245544] mb-2" data-testid={`post-pinned-label-${post.id}`}>
          <Pin size={12} className="fill-[#245544]"/> Pinned
        </div>
      )}
      {/* Author */}
      <div className="flex items-center gap-3 mb-3">
        {author?.avatar_url
          ? <img src={author.avatar_url} className={`rounded-full object-cover ${theme ? "w-11 h-11 ring-2 ring-white shadow" : "w-9 h-9"}`} alt=""/>
          : <div className={`rounded-full bg-[#2E6B57] grid place-items-center text-sm font-bold ${theme ? "w-11 h-11" : "w-9 h-9"}`}>{authorName?.[0]}</div>}
        <div className="flex-1 min-w-0">
          {author?.kind === "client" && !isOwner && user?.role === "client" ? (
            <button onClick={() => navigate(`/client/${author.user_id}`)} className="font-semibold text-sm hover:underline text-left" style={theme ? { fontFamily: "Outfit" } : undefined} data-testid={`post-author-link-${post.id}`}>{authorName}</button>
          ) : (
            <p className="font-semibold text-sm" style={theme ? { fontFamily: "Outfit" } : undefined}>{authorName}</p>
          )}
          {theme ? (
            <div className="flex items-center gap-2 mt-0.5">
              {isProject
                ? <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#E9F0EC] text-[#245544]">Project</span>
                : author?.category && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-black/5 text-[#222]/70">{author.category}</span>}
              <span className="text-xs text-[#222]/40">{new Date(post.created_at).toLocaleDateString()}</span>
            </div>
          ) : (
            <p className="text-xs text-[#222]/50">{new Date(post.created_at).toLocaleDateString()}</p>
          )}
        </div>
        {isOwner && !isProject && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={togglePin} title={post.pinned ? "Unpin" : "Pin to top"}
              className="text-[#222]/40 hover:text-[#245544] p-1" data-testid={`post-pin-toggle-${post.id}`}>
              {post.pinned ? <PinOff size={16}/> : <Pin size={16}/>}
            </button>
            {editable && (
              <>
                <button onClick={() => { setEditText(post.content || ""); setEditing(true); }} title="Edit post"
                  className="text-[#222]/40 hover:text-[#245544] p-1" data-testid={`post-edit-${post.id}`}>
                  <Pencil size={15}/>
                </button>
                <button onClick={deletePost} title="Delete post"
                  className="text-[#222]/40 hover:text-red-500 p-1" data-testid={`post-delete-${post.id}`}>
                  <Trash2 size={15}/>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Wall post-type meta (shared Wall only) */}
      {onWall && (
        <div className="flex flex-wrap items-center gap-2 mb-2 text-xs" data-testid={`wall-meta-${post.id}`}>
          {isRequest && <span className="px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-bold uppercase tracking-wider text-[10px]">Request</span>}
          {post.post_type === "pro_post" && <span className="px-2 py-0.5 rounded-full bg-[#E9F0EC] text-[#245544] font-bold uppercase tracking-wider text-[10px]">Pro Post</span>}
          {post.post_type === "recommendation" && !isProject && <span className="px-2 py-0.5 rounded-full bg-[#E7EEFB] text-[#274690] font-bold uppercase tracking-wider text-[10px]">Recommendation</span>}
          {isRequest && post.category && <span className="px-2 py-0.5 rounded-full bg-black/5 text-[#222]/70 font-semibold">{post.category}</span>}
          {post.approx_distance != null && <span className="text-[#222]/50 flex items-center gap-1"><MapPin size={11}/> ~{post.approx_distance} mi · approx</span>}
          {isRequest && post.timing && <span className="text-[#222]/50 flex items-center gap-1"><Clock size={11}/> {post.timing}</span>}
          {post.status === "closed" && <span className="px-2 py-0.5 rounded-full bg-black/10 text-[#222]/70 font-bold">Closed</span>}
          {post.status === "expired" && <span className="px-2 py-0.5 rounded-full bg-black/10 text-[#222]/70 font-bold">Expired</span>}
          {post.status === "reviewing" && <span className="px-2 py-0.5 rounded-full bg-[#E7EEFB] text-[#274690] font-bold">Reviewing</span>}
          {post.status === "in_progress" && <span className="px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-bold">In progress</span>}
          {post.status === "filled" && <span className="px-2 py-0.5 rounded-full bg-[#2E6B57]/20 text-[#245544] font-bold">Completed</span>}
        </div>
      )}

      {/* Creator-only distribution status (own profile Posts) */}
      {context === "profile" && isOwner && wall && (
        <div className="mb-2 text-[11px] text-[#222]/50 flex items-center gap-1" data-testid={`dist-status-${post.id}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#2E6B57]"/>
          {isRequest
            ? (post.status === "closed" ? "Request closed" : post.status === "expired" ? "Request expired" : `Request open · expires ${post.expires_at ? new Date(post.expires_at).toLocaleDateString() : ""}`)
            : (post.wall_expired ? "Wall expired" : post.on_wall ? "On the Wall" : "Profile only")}
        </div>
      )}

      {/* Moderation menu (non-owner, signed-in) */}
      {user && !isOwner && (
        <div className="relative flex justify-end -mt-1 mb-1">
          <button onClick={() => setMenuOpen(o => !o)} aria-label="Post options" className="p-1.5 rounded-full hover:bg-black/5 text-[#222]/40" data-testid={`post-menu-${post.id}`}><MoreHorizontal size={18}/></button>
          {menuOpen && (
            <div className="absolute right-0 top-8 z-20 bg-white rounded-xl shadow-lg border border-black/10 py-1 w-44 text-sm" data-testid={`post-menu-open-${post.id}`}>
              <button onClick={hidePost} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2" data-testid={`menu-hide-${post.id}`}><EyeOff size={14}/> Hide post</button>
              <button onClick={() => { if (window.confirm(`Mute ${post.author?.name || post.author?.business_name || "this account"}? You'll stop seeing their posts. They won't be notified.`)) doModeration(`/moderation/mute/${post.user_id}`, "Account muted"); }} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2" data-testid={`menu-mute-${post.id}`}><VolumeX size={14}/> Mute account</button>
              <button onClick={() => { if (window.confirm(`Block ${post.author?.name || post.author?.business_name || "this account"}? You won't see each other's posts or be able to message or interact.`)) doModeration(`/moderation/block/${post.user_id}`, "Account blocked"); }} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2 text-red-600" data-testid={`menu-block-${post.id}`}><Ban size={14}/> Block account</button>
              <button onClick={() => { setMenuOpen(false); setReportOpen(true); }} className="w-full text-left px-3 py-2 hover:bg-black/5 flex items-center gap-2" data-testid={`menu-report-${post.id}`}><Flag size={14}/> Report</button>
            </div>
          )}
        </div>
      )}

      {editing ? (
        <div className="mb-3" data-testid={`post-edit-form-${post.id}`}>
          <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3}
            className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
            data-testid={`post-edit-input-${post.id}`}/>
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm rounded-full bg-black/5" data-testid={`post-edit-cancel-${post.id}`}>Cancel</button>
            <button onClick={saveEdit} disabled={busy} className="btn-lime px-4 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50" data-testid={`post-edit-save-${post.id}`}><Check size={14}/> Save changes</button>
          </div>
        </div>
      ) : (
        post.content && <p className="mb-3 text-[#222]/80 leading-relaxed">{post.content}</p>
      )}
      {post.image_url && <img src={post.image_url} onClick={onImageClick} className={`w-full rounded-xl mb-3 ${onImageClick ? "cursor-zoom-in" : ""}`} alt=""/>}
      {(isProject || post.post_type === "recommendation") && post.tagged_pro && <ProMiniCard pro={post.tagged_pro}/>}

      {/* Request footer: open-to-messages + expiration + owner controls */}
      {isRequest && (
        <div className="flex flex-wrap items-center gap-3 mb-1 text-xs text-[#222]/55">
          {post.contact_mode === "message_me"
            ? <span className="text-[#245544] font-semibold">✓ Message me (Pro Plus)</span>
            : <span className="flex items-center gap-1"><Lock size={11}/> I'll reach out myself</span>}
          {post.response_limit && post.response_limit !== "unlimited" && (
            <span className="text-[#222]/50">{post.responses_used ?? 0}/{post.response_limit} responses</span>
          )}
          {post.expires_at && post.status === "open" && <span>Expires {new Date(post.expires_at).toLocaleDateString()}</span>}
          {!isOwner && user?.role === "pro" && post.contact_mode === "message_me" && (post.status === "open" || post.status === "reviewing") && (
            <button onClick={() => navigate(`/messages?to=${post.user_id}&context=${post.id}`)} className="px-3 py-1 rounded-full bg-[#222] text-white font-semibold" data-testid={`request-message-${post.id}`}>Message</button>
          )}
          {isOwner && post.status === "open" && (
            <button onClick={closeRequest} className="ml-auto px-3 py-1 rounded-full border border-black/10 hover:bg-black/5" data-testid={`request-close-${post.id}`}>Close request</button>
          )}
          {isOwner && (post.status === "closed" || post.status === "expired") && (
            <button onClick={renewRequest} className="ml-auto px-3 py-1 rounded-full btn-lime flex items-center gap-1" data-testid={`request-renew-${post.id}`}><RefreshCw size={12}/> Repost</button>
          )}
        </div>
      )}

      {/* Reactions */}
      <div className="flex items-center gap-2 border-t border-black/5 pt-3">
        {reactions.map((r) => {
          const count = postReactions.filter((x) => x.key === r.key).length;
          const mine = user && postReactions.some((x) => x.key === r.key && x.user_id === user.id);
          return (
            <button
              key={r.key}
              onClick={() => react(r.key)}
              className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition border ${mine ? "bg-[#E9F0EC] border-[#2E6B57]" : "bg-white border-black/10 hover:bg-black/5"}`}
              data-testid={`post-react-${r.key}-${post.id}`}
            >
              <span>{r.emoji}</span>
              {count > 0 && <span className="text-xs font-semibold text-[#222]/70">{count}</span>}
            </button>
          );
        })}
        {((wall && user) || (!isOwner && !isOtherPro)) && (
          <button
            onClick={() => setShowChips((s) => !s)}
            className="ml-auto px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 bg-white border border-black/10 hover:bg-black/5"
            data-testid={`post-comment-toggle-${post.id}`}
          >
            <MessageSquarePlus size={15}/> Comment
          </button>
        )}
      </div>

      {/* Wall: free-text comment for any signed-in neighbor */}
      {showChips && wall && user && (
        <div className="mt-3 flex items-center gap-2" data-testid={`wall-comment-box-${post.id}`}>
          <input
            value={freeText}
            maxLength={500}
            onChange={(e) => setFreeText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && freeText.trim()) { comment(freeText.trim()); } }}
            placeholder="Add a comment…"
            className="flex-1 px-3 py-2 border border-black/10 rounded-full text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
            data-testid={`wall-comment-input-${post.id}`}
          />
          <button disabled={busy || !freeText.trim()} onClick={() => comment(freeText.trim())}
            className="btn-lime px-3 py-2 text-sm disabled:opacity-40" data-testid={`wall-comment-send-${post.id}`}>
            <Send size={14}/>
          </button>
        </div>
      )}

      {/* Chip picker (clients / visitors) — non-wall posts */}
      {showChips && !wall && !isOwner && !isOtherPro && (
        <div className="mt-3 space-y-2" data-testid={`post-chip-picker-${post.id}`}>
          <div className="flex flex-wrap gap-2">
            {chips.praise.map((c) => (
              <button key={c} disabled={busy} onClick={() => comment(c)}
                className="px-3 py-1.5 rounded-full text-xs bg-[#E9F0EC] text-[#245544] hover:brightness-95 disabled:opacity-50"
                data-testid={`post-chip-praise-${post.id}`}>{c}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {chips.intent.map((c) => (
              <button key={c} disabled={busy} onClick={() => comment(c)}
                className="px-3 py-1.5 rounded-full text-xs bg-[#FEF3C7] text-[#92400E] hover:brightness-95 disabled:opacity-50"
                data-testid={`post-chip-intent-${post.id}`}>{c}</button>
            ))}
          </div>
          {/* Optional free-text comment */}
          <div className="flex items-center gap-2 pt-1">
            <input
              value={freeText}
              maxLength={300}
              onChange={(e) => setFreeText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && freeText.trim()) { comment(freeText.trim()); setFreeText(""); } }}
              placeholder="…or say it in your own words"
              className="flex-1 px-3 py-2 border border-black/10 rounded-full text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
              data-testid={`post-freetext-${post.id}`}
            />
            <button disabled={busy || !freeText.trim()} onClick={() => { comment(freeText.trim()); setFreeText(""); }}
              className="btn-lime px-3 py-2 text-sm disabled:opacity-40" data-testid={`post-freetext-send-${post.id}`}>
              <Send size={14}/>
            </button>
          </div>
        </div>
      )}

      {/* Comments */}
      {postComments.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`post-comments-${post.id}`}>
          {postComments.map((c) => (
            <div key={c.id} className="flex items-start gap-2 group">
              <div className="w-6 h-6 rounded-full bg-[#F2F4F2] grid place-items-center text-[10px] font-bold overflow-hidden shrink-0">
                {c.user_avatar ? <img src={c.user_avatar} alt="" className="w-full h-full object-cover"/> : c.user_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold mr-2">{c.user_name}{c.role === "pro" ? " · pro" : ""}</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${chipStyle(c.bucket)}`}>{c.text}</span>
              </div>
              {(isOwner || user?.role === "admin") && (
                <button onClick={() => removeComment(c.id)} className="opacity-0 group-hover:opacity-100 text-[#222]/30 hover:text-red-500 shrink-0" data-testid={`post-comment-delete-${c.id}`}>
                  <X size={13}/>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Owner / tagged-pro reply box */}
      {canReply && !wall && (
        <div className="mt-3 flex items-center gap-2">
          <input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendReply()}
            placeholder={isProject && !isOwner ? "Say thanks to your client…" : "Reply to your neighbors…"}
            className="flex-1 px-3 py-2 border border-black/10 rounded-full text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
            data-testid={`post-reply-input-${post.id}`}
          />
          <button onClick={sendReply} disabled={busy || !reply.trim()} className="btn-lime px-3 py-2 text-sm disabled:opacity-40" data-testid={`post-reply-send-${post.id}`}>
            <Send size={14}/>
          </button>
        </div>
      )}

      {reportOpen && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setReportOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid={`report-modal-${post.id}`}>
            <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Report this post</h3>
            <div className="space-y-1.5 mb-3">
              {[["spam", "Spam"], ["harassment", "Harassment"], ["scam", "Scam or fraud"], ["private_info", "Private information"], ["unsafe", "Unsafe or inappropriate"], ["false_info", "False business information"], ["other", "Other"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name={`report-${post.id}`} checked={reportReason === k} onChange={() => setReportReason(k)} data-testid={`report-reason-${k}`}/> {label}
                </label>
              ))}
            </div>
            <textarea value={reportNote} onChange={e => setReportNote(e.target.value)} rows={2} placeholder="Add a short note (optional)"
              className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm mb-3 outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid={`report-note-${post.id}`}/>
            <div className="flex justify-end gap-2">
              <button onClick={() => setReportOpen(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button onClick={submitReport} className="btn-lime px-4 py-2 text-sm" data-testid={`report-submit-${post.id}`}>Submit Report</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostCard;
